
import { Options } from "vue-class-component";
import { Emit, Prop, Watch } from "vue-property-decorator";
import FormMixin from "../../helpers/mixins/form";
import {
  ApplicantApi,
  GetApplicantProfile,
  UpdateApplicantRequest,
  Employee,
  CreateEmployee,
  ContractTypeEnum,
  ApplicantStatsEnum
} from "../../autogen";
import { Field, Form } from "vee-validate";
import FieldLabel from "../atoms/FieldLabel.vue";
import * as yup from "yup";
import FieldError from "../atoms/FieldError.vue";
import FormError from "../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import EmployeeFormFields from "../molecules/EmployeeFormFields.vue";

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  contractType: string;
  career: "new" | "mid";
  status: string;
  departmentId: string;
  occupationId: string;
};
@Options({
  components: {
    Field,
    Form,
    FieldLabel,
    FieldError,
    ActionButton,
    FormError,
    EmployeeFormFields
  }
})
export default class ApplicantForm extends FormMixin {
  departments = this.storeInstance.getDepartments({ withoutAll: true });
  occupations = this.storeInstance.getOccupations();
  @Prop({ type: Object }) applicant?: GetApplicantProfile;
  formName = "応募者";
  formValues: FormValues = {
    firstName: "",
    lastName: "",
    email: "",
    contractType: "employee",
    career: "new",
    status: "screening",
    departmentId: "",
    occupationId: ""
  };
  currentStatus = "screening";
  conversion = false;
  employee?: Partial<Employee>;
  validationSchema!: yup.AnyObjectSchema;
  /** フォーム変更可否フラグ */
  isEditable = false;
  isEmpEditable = false;
  enableConvert = [ApplicantStatsEnum.Hired, ApplicantStatsEnum.Joined];
  contractOptions = Object.entries(ContractTypeEnum).map(([code, name]) => ({ code, name }));
  contractEmployee = "";

  beforeCreate() {
    this.validationSchema = yup.object({
      firstName: yup.string().required(this.$t("input_required", { field: "名" })),
      lastName: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "姓" })),
      email: yup
        .string()
        .nullable(true)
        .email(this.$t("input_email")),
      departmentId: yup.string().test("departmentId", this.$t("input_required", { field: "部署か職種" }), function(value) {
        return this.parent.occupationId || value;
      }),
      occupationId: yup.string().test("occupationId", this.$t("input_required", { field: "部署か職種" }), function(value) {
        return this.parent.departmentId || value;
      }),
      contractType: yup.string().required(this.$t("input_required", { field: "雇用形態" }))
    });
  }
  async beforeMount() {
    this.departments = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
    if (this.applicant) {
      this.formValues = (this.applicant as unknown) as FormValues;
    }
    this.contractEmployee = this.formValues.contractType;
    this.isEditable = this.$role.isEditable("applicant");
    this.isEmpEditable = this.$role.isEditable("employee");
  }
  async onBeforeSubmit(values: FormValues) {
    await this.onSubmit(async () => {
      Object.assign(values, {
        career: ["employee"].includes(values.contractType as string) ? values.career : ""
      });
      if (this.applicant) {
        await new ApplicantApi().updateApplicant({
          applicantId: this.applicant!.id,
          updateApplicantRequest: values as UpdateApplicantRequest
        });
      } else {
        await new ApplicantApi().createApplicant({
          createApplicantRequest: values as UpdateApplicantRequest
        });
      }
      this.$emit("submitted");
    });
  }
  async onDeleteButtonClick() {
    this.onDelete(async () => {
      await new ApplicantApi().deleteApplicant({
        applicantId: this.applicant!.id
      });
    });
  }
  async convert(values: CreateEmployee) {
    await new ApplicantApi().toEmployee({
      applicantId: this.applicant!.id,
      body: values
    });
  }
  @Emit("submitted")
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onConversionSubmitted() {}
  onConvertButtonClick() {
    if (!this.isEditable && !this.isEmpEditable) return;
    this.conversion = true;
    this.employee = Object.assign({}, {
      lastName: this.applicant?.lastName,
      firstName: this.applicant?.firstName,
      gender: this.applicant?.gender,
      email: this.applicant?.email,
      contractType: this.applicant?.contractType,
      career: this.applicant?.career,
      enrollment: "enrolled",
      departmentId: this.applicant!.department?.id,
      occupationId: this.applicant!.occupation?.id
    } as Employee);
  }
  onStatusChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.currentStatus = target.value;
  }
  @Watch("storeInstance.state.departments")
  stateDepartmentsChange() {
    this.departments = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
  }
}
